import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Periode } from '../types';
import periodeService from '../services/periodeService';
import { Button, Input, Select } from './ui';

const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/** Letzter möglicher Tag je Monat – der 29.02. ist erlaubt (Schaltjahr), der 30.02. nicht. */
const maxTag = (monat: number): number => {
  if (monat === 2) return 29;
  return [4, 6, 9, 11].includes(monat) ? 30 : 31;
};

const NEU = '__neu__';

/**
 * Auswahl des Leistungszeitpunkts am Artikel (intern: Periode). Eine eigene Seite dafür gibt es
 * bewusst nicht mehr – ein neuer Zeitpunkt wird direkt hier über „+ Neuer Leistungszeitpunkt …“
 * angelegt und gleich ausgewählt.
 */
const LeistungszeitpunktAuswahl: React.FC<{
  value: string;
  onChange: (periodeId: string) => void;
}> = ({ value, onChange }) => {
  const [perioden, setPerioden] = useState<Periode[]>([]);
  const [neu, setNeu] = useState(false);
  const [name, setName] = useState('');
  const [monat, setMonat] = useState('');
  const [tag, setTag] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Nur Zeitpunkte mit Datum – ohne Termin taugt einer nicht für einen Artikel.
    periodeService.getAll().then(ps => setPerioden(ps.filter(p => p.terminText))).catch(() => setPerioden([]));
  }, []);

  const waehle = (wert: string) => {
    if (wert === NEU) { setNeu(true); return; }
    onChange(wert);
  };

  const handleMonat = (wert: string) => {
    setMonat(wert);
    if (tag !== '' && wert !== '' && parseInt(tag, 10) > maxTag(parseInt(wert, 10))) setTag(String(maxTag(parseInt(wert, 10))));
  };

  const anlegen = async () => {
    if (!name.trim() || monat === '' || tag === '') { toast.error('Bitte Name, Tag und Monat angeben'); return; }
    setSaving(true);
    try {
      const p = await periodeService.create({ name: name.trim(), tag: parseInt(tag, 10), monat: parseInt(monat, 10), notizen: null });
      setPerioden(ps => [...ps, p]);
      onChange(String(p.id));
      setNeu(false); setName(''); setMonat(''); setTag('');
      toast.success(`Leistungszeitpunkt „${p.name}“ angelegt`);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Leistungszeitpunkt konnte nicht angelegt werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Select value={neu ? NEU : value} onChange={e => waehle(e.target.value)}>
        <option value="">— keiner —</option>
        {perioden.map(p => <option key={p.id} value={p.id}>{p.name} ({p.terminText})</option>)}
        <option value={NEU}>+ Neuer Leistungszeitpunkt …</option>
      </Select>
      {neu && (
        <div className="mt-2 space-y-2 rounded-lg border border-brand-200 bg-brand-50/40 p-3">
          <Input autoFocus maxLength={100} placeholder="Name, z. B. Frühjahr" value={name} onChange={e => setName(e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <Select value={tag} disabled={monat === ''} onChange={e => setTag(e.target.value)}>
              <option value="">Tag</option>
              {monat !== '' && Array.from({ length: maxTag(parseInt(monat, 10)) }, (_, i) => i + 1)
                .map(t => <option key={t} value={t}>{t}.</option>)}
            </Select>
            <Select value={monat} onChange={e => handleMonat(e.target.value)}>
              <option value="">Monat</option>
              {MONATE.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => setNeu(false)}>Abbrechen</Button>
            <Button type="button" size="sm" loading={saving} onClick={anlegen}>Leistungszeitpunkt anlegen</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeistungszeitpunktAuswahl;
